import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import ClosedBanner from "./components/ClosedBanner";
import "./i18n";

// SITE CLOSED - Set to true to show closed banner
const SITE_CLOSED = true;

// Public Pages
import HomePage from "./pages/HomePage";
import VehiclesPage from "./pages/VehiclesPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import CheckoutPage from "./pages/CheckoutPage";
import BookingSuccessPage from "./pages/BookingSuccessPage";
import FleetRentalPage from "./pages/FleetRentalPage";
import LongTermPage from "./pages/LongTermPage";
import TermsPage from "./pages/TermsPage";
import ReviewsPage from "./pages/ReviewsPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVehicles from "./pages/admin/AdminVehicles";
import AdminVehicleBlocks from "./pages/admin/AdminVehicleBlocks";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminSettings from "./pages/admin/AdminSettings";
import ForgotPasswordPage from "./pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "./pages/admin/ResetPasswordPage";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-white/50">Loading...</p>
      </div>
    );
  }
  
  if (!admin) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Admin Route - redirects to dashboard if already logged in
const AdminRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-white/50">Loading...</p>
      </div>
    );
  }
  
  if (admin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
};

function App() {
  // Show closed banner for public pages (admin still accessible)
  const isAdminPath = window.location.pathname.startsWith('/admin');
  
  if (SITE_CLOSED && !isAdminPath) {
    return <ClosedBanner />;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/vehicles" element={<VehiclesPage />} />
          <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
          <Route path="/booking/checkout/:vehicleId" element={<CheckoutPage />} />
          <Route path="/booking/success" element={<BookingSuccessPage />} />
          <Route path="/fleet-rental" element={<FleetRentalPage />} />
          <Route path="/long-term" element={<LongTermPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          
          {/* Admin Login */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLoginPage />
            </AdminRoute>
          } />
          
          {/* Password Reset Routes (public) */}
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="vehicles" element={<AdminVehicles />} />
            <Route path="vehicle-blocks" element={<AdminVehicleBlocks />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="inquiries" element={<AdminInquiries />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
